import { formatNameLastFirst, formatPhoneNumber } from '@strata/helper-functions';
import { Dialog } from '@strata/react-dialog';
import { InlineMessage } from '@strata/react-inline-message';
import { AddressDetailLabelValue, DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { type ComponentProps } from 'react';

import { type PrescriberLookupEntry } from 'store/api/referenceApi';

interface PrescriberLookupDialogProps extends Required<
    Pick<ComponentProps<typeof Dialog>, 'isOpen' | 'onSecondaryButtonClick'>
> {
    prescriberLookupResponse?: PrescriberLookupEntry[];
    enteredPrescriberNpi: string;
    onPrimaryButtonClick: (selectedPrescriber?: PrescriberLookupEntry) => void;
}

export const PrescriberLookupDialog = ({
    prescriberLookupResponse = undefined,
    enteredPrescriberNpi,
    isOpen,
    onPrimaryButtonClick,
    onSecondaryButtonClick,
}: PrescriberLookupDialogProps) => {
    const prescriber = prescriberLookupResponse?.[0];
    const prescriberFound = prescriber != null;
    return (
        <Dialog
            dialogTitle={prescriberFound ? 'Prescriber Found' : 'Prescriber Not Found'}
            isOpen={isOpen}
            primaryButtonLabel={
                prescriberFound ? 'Confirm Prescriber' : 'Add Unverified Prescriber'
            }
            secondaryButtonLabel="Return To Intake Form"
            onPrimaryButtonClick={() => onPrimaryButtonClick(prescriber)}
            onSecondaryButtonClick={onSecondaryButtonClick}
            dialogContent={
                <div className="flex__gap--large" style={{ flexDirection: 'column' }}>
                    {prescriberFound ? (
                        <>
                            <InlineMessage
                                intent={InlineMessage.Intent.SUCCESS}
                                label="Prescriber found."
                            />
                            <div style={{ display: 'flex' }}>
                                <DetailLabelValuePair
                                    style={{ width: '50%' }}
                                    label="Prescriber NPI"
                                    value={prescriber.prescriberNpi}
                                />
                                <DetailLabelValuePair
                                    label="Prescriber"
                                    value={formatNameLastFirst(
                                        prescriber.lastName,
                                        prescriber.firstName,
                                    )}
                                />
                            </div>
                            <div style={{ display: 'flex' }}>
                                <DetailLabelValuePair
                                    style={{ width: '50%' }}
                                    label="Phone"
                                    value={formatPhoneNumber(prescriber.telephoneNumber)}
                                />
                                <DetailLabelValuePair
                                    label="Fax"
                                    value={formatPhoneNumber(prescriber.faxNumber)}
                                />
                            </div>
                            <AddressDetailLabelValue
                                address1={prescriber.address1}
                                address2={prescriber.address2}
                                city={prescriber.city}
                                state={prescriber.state}
                                zip={prescriber.zip}
                            />
                        </>
                    ) : (
                        <>
                            <InlineMessage
                                intent={InlineMessage.Intent.ERROR}
                                label="Prescriber not found."
                            />
                            <div>
                                Ensure you entered the correct prescriber NPI before adding an
                                unverified prescriber.
                            </div>
                            <DetailLabelValuePair
                                style={{ width: '50%' }}
                                label="Prescriber NPI"
                                value={enteredPrescriberNpi}
                            />
                        </>
                    )}
                </div>
            }
        />
    );
};
